/**
 * ObondoTrialBanner — Directive 4 Amendment · Spec §2.B.
 *
 * Sleek Inter-styled sticky top banner rendered on the trader dashboard
 * whenever `subscription_tier === 'advanced' && trial_status === 'ACTIVE'`.
 * Colour scale blue → orange as days-remaining crosses ≤ 7.
 *
 * Copy is fully localized via `beco-i18n.js` and flips to RTL for AR.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useBecoCopy } from "@/lib/beco-i18n";
import { CalendarClock, Sparkles, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const DISMISS_KEY = "obondo-trial-banner-dismissed-until";

function isDismissedNow() {
    try {
        const until = Number(localStorage.getItem(DISMISS_KEY) || 0);
        return until && Date.now() < until;
    } catch { return false; }
}

function dismissForHours(hours) {
    try {
        localStorage.setItem(DISMISS_KEY, String(Date.now() + hours * 3600 * 1000));
    } catch { /* ignore */ }
}

export default function ObondoTrialBanner() {
    const [status, setStatus] = useState(null);
    const [dismissed, setDismissed] = useState(isDismissedNow());
    const { t, isRtl } = useBecoCopy();

    useEffect(() => {
        let cancelled = false;
        api.get("/trial/status")
            .then((r) => { if (!cancelled) setStatus(r.data); })
            .catch(() => { if (!cancelled) setStatus(null); });
        return () => { cancelled = true; };
    }, []);

    if (!status?.active || dismissed) return null;
    const daysLeft = Number(status.days_remaining ?? 0);
    // Spec §2.B · blue-to-orange transition at ≤7 days
    const urgent   = daysLeft <= 7;
    const critical = daysLeft <= 3;

    const toneCls = critical
        ? "border-rose-500/40 bg-rose-500/10 text-rose-700 dark:text-rose-200"
        : urgent
            ? "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-200"
            : "border-sky-500/40 bg-sky-500/10 text-sky-700 dark:text-sky-200";

    const daysLabel = daysLeft === 1
        ? t("trial.banner.day_left", "1 day remaining")
        : t("trial.banner.days_left", "{N} days remaining").replace("{N}", String(daysLeft));

    return (
        <div
            className={`sticky top-14 z-10 border-b ${toneCls} backdrop-blur-sm`}
            dir={isRtl ? "rtl" : undefined}
            data-testid="obondo-trial-banner"
            data-days-remaining={daysLeft}
        >
            <div className="max-w-[1600px] mx-auto px-3 sm:px-4 md:px-6 py-2 flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                    <div className="p-1.5 rounded-lg bg-background/40 shrink-0">
                        {critical
                            ? <CalendarClock className="w-4 h-4" />
                            : <Sparkles className="w-4 h-4" />}
                    </div>
                    <div className="min-w-0">
                        <div className="text-xs font-bold tracking-tight">
                            {t("trial.banner.title", "Obondo 7-Day Free Trial")}
                        </div>
                        <div className="text-[11px] opacity-90" data-testid="obondo-trial-days-remaining">
                            {daysLabel}
                            {status.monthly_fee_display && (
                                <span className="opacity-70"> · {status.monthly_fee_display}</span>
                            )}
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                    <Link to="/app/subscription">
                        <Button
                            size="sm"
                            variant={critical ? "destructive" : "default"}
                            className="text-xs h-7"
                            data-testid="obondo-trial-manage-btn"
                        >
                            {t("trial.banner.manage", "View plan details")}
                        </Button>
                    </Link>
                    <button
                        onClick={() => { dismissForHours(critical ? 6 : 24); setDismissed(true); }}
                        className="p-1.5 rounded-md hover:bg-background/40 transition-colors"
                        aria-label="Dismiss"
                        data-testid="obondo-trial-dismiss-btn"
                    >
                        <X className="w-3.5 h-3.5" />
                    </button>
                </div>
            </div>
        </div>
    );
}
